// BombBomb integration — the team's video provider. One API key for the whole
// team, stored like Follow Up Boss's: integrations/bombBomb in Firestore
// ({ apiKey, connectedBy, connectedAt }), read by the functions-api backend
// which proxies the BombBomb API (no key or CORS in the browser).
import { doc, getDoc, setDoc } from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";

const API_BASE = "https://us-central1-team-edelstein.cloudfunctions.net/api";
const INTEGRATION_DOC = ["integrations", "bombBomb"];

// BombBomb's classic hosted video landing page.
// CONFIRM ON FIRST TEST: exact landing URL shape for Henry's account.
export function bombBombPageUrl(videoId) {
  return `https://bbemaildelivery.com/bbext/?p=video_land&id=${encodeURIComponent(videoId)}`;
}

// Tolerant mapper: BombBomb's V2 and legacy APIs name fields differently, so
// accept the known variants and produce one stable shape.
export function normalizeBombBombVideos(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((v) => v && typeof v === "object")
    .map((v) => {
      const id = v.id || v.videoId || v.video_id || "";
      if (!id) return null;
      return {
        id: String(id),
        name: v.name || v.title || v.video_name || "Untitled Video",
        thumbnail:
          v.thumbnailUrl || v.thumbUrl || v.thumb_url || v.thumbnail || "",
        animatedThumbnail:
          v.animatedThumbUrl ||
          v.animated_thumb_url ||
          v.animatedGifUrl ||
          v.gifUrl ||
          "",
        pageUrl:
          v.shortWebPageUrl ||
          v.webPageUrl ||
          v.videoPageUrl ||
          bombBombPageUrl(id),
      };
    })
    .filter(Boolean);
}

// ---- Firestore (connection state) ----

export async function getBombBombIntegration() {
  try {
    const snap = await getDoc(doc(firestored, ...INTEGRATION_DOC));
    return snap.exists() ? snap.data() : null;
  } catch (e) {
    console.error("getBombBombIntegration failed:", e);
    return null;
  }
}

export async function saveBombBombIntegration(apiKey, userId) {
  await setDoc(
    doc(firestored, ...INTEGRATION_DOC),
    {
      apiKey: String(apiKey || "").trim(),
      connectedBy: userId || "",
      connectedAt: Date.now(),
    },
    { merge: true },
  );
}

// ---- Backend proxy ----

// Returns { ok, videos } or { ok: false, error }.
export async function fetchBombBombVideos() {
  try {
    const res = await fetch(`${API_BASE}/bombbomb-videos`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      // A 404 means the live backend predates this feature.
      const fallback =
        res.status === 404
          ? "BombBomb isn't live yet — the email backend needs its update."
          : `HTTP ${res.status}`;
      return { ok: false, error: data.error || fallback };
    }
    return { ok: true, videos: normalizeBombBombVideos(data.videos) };
  } catch (e) {
    return { ok: false, error: e?.message || "Network error" };
  }
}
